import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AssignmentTypeEnum } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import {
  QaiFieldComponent,
  QaiFloatingSelectComponent,
  QaiTooltipComponent,
  QaiInputComponent,
  QaiItemGridComponent,
  QaiTextareaComponent,
  TextareaAutosizeDirective,
  QaiFloatingDirective,
  QaiToggleSwitchWrapperComponent,
  QaiDatepickerComponent,
  QaiIconComponent,
  QaiIconButtonComponent,
  QaiSlotDirective,
} from '@qai/ui';
import { toDateInputString } from 'src/app/utils/date-parser.util';
import { AssignmentReactiveFormService } from '../../services/assignment-reactive-form.service';
import { Subscription } from 'rxjs';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AppConstants } from 'src/app/app-constants';
import { AccountFeatureService } from 'src/app/services/account-feature/account-feature.service';
import { LocaleService } from 'src/app/services/locale.service';

@Component({
  selector: 'assignment-information-view',
  templateUrl: './assignment-information-view.component.html',
  styleUrls: ['./assignment-information-view.component.scss'],
  standalone: true,
  imports: [
    QaiFieldComponent,
    QaiInputComponent,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiItemGridComponent,
    QaiFloatingSelectComponent,
    ReactiveFormsModule,
    QaiDatepickerComponent,
    QaiToggleSwitchWrapperComponent,
    QaiIconComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
    QaiIconButtonComponent,
    QaiSlotDirective,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AssignmentInformationViewComponent
  implements OnInit, OnChanges, OnDestroy
{
  minStartDate: string = toDateInputString(new Date().toISOString());
  minEndDate: string = AssignmentReactiveFormService.calculateMinEndDate();
  readOnly: boolean = false;
  readonly datePickerFormatter = (date: Date): string => this.localeService.formatDate(date);
  isClosedOrArchived: boolean = false;
  @Input() form!: FormGroup;
  @Input() callUuid: string = '';
  @Input() isNewAssignment: boolean = false;
  @Input() assignmentTypeOptions: {
    label: string;
    value: AssignmentTypeEnum;
  }[] = [];
  isAssignmentNameAlreadyExists: boolean = false;
  // Gates whether the Hide Results toggle is offered at all — the account's own choice to make
  // the per-assignment option available, not a value applied to every assignment.
  hideResultsFeatureEnabled: boolean = false;

  get endDateError(): string {
    return this.translocoService.translate('simulationBuilder.END_DATE_RANGE_ERROR');
  }

  @Output() valueChange = new EventEmitter<any>();

  private subscriptions: Subscription[] = [];

  constructor(
    private assignmentReactiveFormService: AssignmentReactiveFormService,
    private translocoService: TranslocoService,
    private accountFeatureService: AccountFeatureService,
    private localeService: LocaleService
  ) {}

  ngOnInit() {
    this.accountFeatureService
      .isFeatureActive(AppConstants.ACCOUNT_FEATURE_HIDE_RESULTS)
      .then((active) => {
        this.hideResultsFeatureEnabled = active;
      });

    if (this.form) {
      this.initializeComponent();
      this.setupFormSubscriptions();
      this.handleReadOnlyMode();
      this.setupAssignmentNameConflictSubscription();

      this.validateScoreCriteria();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['form'] &&
      changes['form'].currentValue &&
      !changes['form'].firstChange
    ) {
      this.cleanupSubscriptions();
      this.resetFormState();
      this.initializeComponent();
      this.setupFormSubscriptions();
      this.handleReadOnlyMode();
      this.setupAssignmentNameConflictSubscription();

      this.validateScoreCriteria();
      this.triggerFormValidation();
    }
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
  }

  isCertificationType(): boolean {
    return (
      this.form?.get('moduleAssignmentType')?.value ===
      AssignmentTypeEnum.CERTIFICATION
    );
  }

  isPersonalizedPracticeType(): boolean {
    return (
      this.form?.get('moduleAssignmentType')?.value ===
      AssignmentTypeEnum.PERSONALIZED_PRACTICE
    );
  }

  shouldHideFieldsForPersonalizedPractice(): boolean {
    return this.isPersonalizedPracticeType() && !this.isNewAssignment;
  }

  get pauseEnabledChecked(): boolean {
    return this.form?.get('pauseEnabled')?.value ?? true;
  }

  onPauseEnabledChange(checked: boolean) {
    this.form?.get('pauseEnabled')?.setValue(checked);
  }

  get hideResultsForLearnersChecked(): boolean {
    return this.form?.get('hideResultsForLearners')?.value ?? false;
  }

  onHideResultsForLearnersChange(checked: boolean) {
    this.form?.get('hideResultsForLearners')?.setValue(checked);
  }

  get showMissedFactsChecked(): boolean {
    return this.form?.get('showMissedFacts')?.value ?? false;
  }

  onshowMissedFactsChange(checked: boolean) {
    this.form?.get('showMissedFacts')?.setValue(checked);
  }

  get endDateErrorMessage(): string {
    return this.form?.get('endDate')?.hasError('invalidDateRange')
      ? this.endDateError
      : '';
  }

  onDateChange(controlName: string, value: string | null): void {
    const formattedValue = value ?? '';
    this.form?.get(controlName)?.setValue(formattedValue);

    if (controlName === 'startDate') {
      this.handleStartDateChange(formattedValue);
    }

    if (controlName === 'endDate') {
      this.validateDateRange();
    }
  }

  private initializeComponent(): void {
    this.initializeMinEndDate();
    this.updateConditionalValidators(
      this.form.get('moduleAssignmentType')?.value
    );
    this.resetFormValidationState();
  }

  private setupFormSubscriptions(): void {
    this.setupAssignmentTypeSubscription();
    this.setupFormValueChangesSubscription();
    this.setupAssignmentNameSubscription();

    this.validateScoreCriteria();
  }

  private setupAssignmentTypeSubscription(): void {
    const subscription = this.form
      .get('moduleAssignmentType')
      ?.valueChanges.subscribe((type) => {
        this.updateConditionalValidators(type);
      });
    if (subscription) {
      this.subscriptions.push(subscription);
    }
  }

  private setupFormValueChangesSubscription(): void {
    const subscription = this.form.valueChanges.subscribe((val) => {
      // Include disabled controls in the emitted value
      const fullValue = this.form.getRawValue();
      this.valueChange.emit(fullValue);
      this.validateScoreCriteria();
    });
    if (subscription) {
      this.subscriptions.push(subscription);
    }

    this.validateScoreCriteria();
  }

  private setupAssignmentNameSubscription(): void {
    const nameControl = this.form.get('moduleAssignmentName');
    if (nameControl) {
      const subscription = nameControl.valueChanges.subscribe(() => {
        this.assignmentReactiveFormService.setAssignmentNameConflict(false);
      });
      if (subscription) {
        this.subscriptions.push(subscription);
      }
    }
  }

  private setupAssignmentNameConflictSubscription(): void {
    const subscription =
      this.assignmentReactiveFormService.assignmentNameConflict.subscribe(
        (conflict) => {
          this.isAssignmentNameAlreadyExists = conflict;
        }
      );
    if (subscription) {
      this.subscriptions.push(subscription);
    }
  }

  private cleanupSubscriptions(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions = [];
  }

  private handleReadOnlyMode(): void {
    if (this.callUuid) {
      this.readOnly = this.assignmentReactiveFormService.isReadOnly(this.callUuid);
      this.isClosedOrArchived = this.assignmentReactiveFormService.isClosedOrArchived(this.callUuid);
      this.setReadOnlyMode();
    }
  }

  private initializeMinEndDate(): void {
    const startDateValue = this.form.get('startDate')?.value;
    this.minEndDate =
      AssignmentReactiveFormService.calculateMinEndDate(startDateValue);
  }

  private handleStartDateChange(startDateValue: string): void {
    this.minEndDate =
      AssignmentReactiveFormService.calculateMinEndDate(startDateValue);
    this.clearInvalidEndDate();
  }

  private clearInvalidEndDate(): void {
    const endDateControl = this.form?.get('endDate');
    if (!endDateControl?.value) return;

    const startDateValue = this.form?.get('startDate')?.value;
    if (!startDateValue) return;

    const isValid = AssignmentReactiveFormService.isEndDateValid(
      startDateValue,
      endDateControl.value
    );
    if (!isValid) {
      endDateControl.setValue('', { emitEvent: false });
    }
  }

  private validateScoreCriteria(): void {
    const scoreCriteria = this.form.get('scoreCriteria');
    if (!scoreCriteria) return;

    if (scoreCriteria.value > 5) {
      scoreCriteria.setErrors({ maxExceeded: true });
    } else if (scoreCriteria.hasError('maxExceeded')) {
      scoreCriteria.setErrors(null);
    }
  }

  private validateDateRange(): void {
    const endDateControl = this.form.get('endDate');
    endDateControl?.updateValueAndValidity();
  }

  private updateConditionalValidators(type: AssignmentTypeEnum): void {
    const endDateControl = this.form.get('endDate');
    const scoreControl = this.form.get('scoreCriteria');

    const endDateValidators =
      type === AssignmentTypeEnum.CERTIFICATION
        ? [
            Validators.required,
            AssignmentReactiveFormService.dateRangeValidator,
          ]
        : [AssignmentReactiveFormService.dateRangeValidator];

    const scoreValidators =
      type === AssignmentTypeEnum.CERTIFICATION
        ? [Validators.required, Validators.min(1)]
        : [];

    endDateControl?.setValidators(endDateValidators);
    scoreControl?.setValidators(scoreValidators);

    endDateControl?.updateValueAndValidity();
    scoreControl?.updateValueAndValidity();
  }

  private setReadOnlyMode(): void {
    this.assignmentReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.form.get('scoreCriteria')
    );
  }

  private resetFormValidationState(): void {
    const scoreCriteria = this.form.get('scoreCriteria');
    if (scoreCriteria?.hasError('maxExceeded')) {
      scoreCriteria.setErrors(null);
    }

    this.form.updateValueAndValidity();
  }

  private resetFormState(): void {
    this.isAssignmentNameAlreadyExists = false;
    this.minStartDate = toDateInputString(new Date().toISOString());
    this.minEndDate = AssignmentReactiveFormService.calculateMinEndDate();

    this.resetFormValidationState();
  }

  private triggerFormValidation(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        control.updateValueAndValidity();
      }
    });

    this.form.updateValueAndValidity();
  }

}
